/**
 * EE API Role Rights 管理頁（Enterprise Edition only）
 *
 * 兩欄式介面（類似 CE role_rights）：
 *   左欄：API 角色清單（選擇）
 *   右欄：該角色對所有已註冊 API 服務（func_type=3）的呼叫權限勾選
 *
 * 對應後端：
 *   GET  /api/ee/api_role_rights/available_services
 *   GET  /api/ee/api_role_rights/by_role/{id}
 *   PUT  /api/ee/api_role_rights/by_role/{id}（批次儲存）
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, List, ListItem, ListItemButton, ListItemText,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Checkbox, Button, Stack, Chip, Divider,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { getI18nValue } from '../../utils/i18nHelper';
import { ApiRole, listApiRoles } from '../../services/eeApiRolesService';
import {
  AvailableService, listAvailableServices,
  listRightsByRole, saveRightsByRole,
} from '../../services/eeApiRoleRightsService';


const EeApiRoleRightsPage: React.FC = () => {
  const { i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();

  const [roles, setRoles] = useState<ApiRole[]>([]);
  const [services, setServices] = useState<AvailableService[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  // sf_id → is_call（true 代表已勾選）
  const [rights, setRights] = useState<Record<number, boolean>>({});
  const [dirty, setDirty] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [rolesData, svcData] = await Promise.all([
        listApiRoles(),
        listAvailableServices(),
      ]);
      setRoles(rolesData);
      setServices(svcData);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const loadRightsFor = useCallback(async (roleId: number) => {
    try {
      const data = await listRightsByRole(roleId);
      const map: Record<number, boolean> = {};
      for (const r of data) map[r.system_function_id] = r.is_call;
      setRights(map);
      setDirty(false);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => {
    if (selectedRoleId !== null) loadRightsFor(selectedRoleId);
    else { setRights({}); setDirty(false); }
  }, [selectedRoleId, loadRightsFor]);

  const toggle = (sfId: number) => {
    setRights(prev => ({ ...prev, [sfId]: !prev[sfId] }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (selectedRoleId === null) return;
    const payload = services
      .filter(s => rights[s.system_function_id])
      .map(s => ({
        system_function_id: s.system_function_id,
        func_code: s.func_code,
        is_call: true,
      }));
    try {
      await saveRightsByRole(selectedRoleId, payload);
      showSuccess('SYS040004', { name: `api_role_id=${selectedRoleId}` });
      setDirty(false);
      loadRightsFor(selectedRoleId);
    } catch (err) { showApiError(err); }
  };

  const selectedRole = roles.find(r => r.id === selectedRoleId);

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_api_role_rights" fallback="API 角色權限" />

      <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'stretch' }}>
        {/* 左欄：角色清單 */}
        <Paper sx={{ width: 320, p: 1 }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>API 角色</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>
              重整
            </Button>
          </Stack>
          <Divider />
          <List dense sx={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
            {roles.map(r => (
              <ListItem key={r.id} disablePadding>
                <ListItemButton selected={r.id === selectedRoleId}
                                onClick={() => {
                                  if (dirty && !window.confirm('有未儲存變更，切換將放棄變更。繼續？')) return;
                                  setSelectedRoleId(r.id);
                                }}>
                  <ListItemText
                    primary={<>
                      <code style={{ fontSize: '0.85em' }}>{r.role_code}</code>
                      {!r.is_active && <Chip label="停用" size="small" sx={{ ml: 1 }} />}
                    </>}
                    secondary={getI18nValue(r.role_name, i18n.language) || ''}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {roles.length === 0 && (
              <ListItem><ListItemText secondary="（尚無 API 角色，請先到 API 角色管理建立）" /></ListItem>
            )}
          </List>
        </Paper>

        {/* 右欄：權限設定 */}
        <Paper sx={{ flex: 1, p: 2 }}>
          {selectedRole ? (
            <>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {getI18nValue(selectedRole.role_name, i18n.language) || selectedRole.role_code}
                  <Chip label={selectedRole.role_code} size="small" sx={{ ml: 1 }} />
                </Typography>
                <Button variant="contained" size="small" startIcon={<SaveIcon />}
                        onClick={handleSave} disabled={!dirty}>
                  儲存變更
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                勾選代表此角色可呼叫該 API 服務（is_call=true）。未勾選項目會同步刪除 role_rights。
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={60} align="center">允許</TableCell>
                      <TableCell>func_code</TableCell>
                      <TableCell>名稱</TableCell>
                      <TableCell>說明</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {services.map(s => (
                      <TableRow key={s.system_function_id} hover>
                        <TableCell align="center">
                          <Checkbox size="small"
                                    checked={!!rights[s.system_function_id]}
                                    onChange={() => toggle(s.system_function_id)} />
                        </TableCell>
                        <TableCell><code>{s.func_code}</code></TableCell>
                        <TableCell>{getI18nValue(s.func_name, i18n.language) || s.func_code}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{s.description || '—'}</TableCell>
                      </TableRow>
                    ))}
                    {services.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        尚無已註冊的 API 服務（@api_service 尚未裝飾任何 handler）
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              請從左側選擇一個 API 角色以編輯其權限
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default EeApiRoleRightsPage;
